import { useState } from 'react'
import { Switch, Route, Link, useLocation } from 'wouter'
import { useAdminAuth } from './hooks/useAdminAPI'
import { AdminIcon } from './components/AdminIcon'
import { AdminButton } from './components/AdminButton'
import { Overview } from './views/Overview'
import { Clusters } from './views/Clusters'
import { ClusterGraph } from './views/ClusterGraph'
import { Offers } from './views/Offers'
import { ScrapeRuns } from './views/ScrapeRuns'
import { SourceBreakdown } from './views/SourceBreakdown'
import { Disagreements } from './views/Disagreements'
import styles from './AdminShell.module.css'

const NAV_GROUPS = [
  {
    label: 'Monitor',
    items: [
      { path: '/', label: 'Overview', icon: 'overview', exact: true },
      { path: '/sources', label: 'Sources', icon: 'sources' },
      { path: '/runs', label: 'Scrape runs', icon: 'runs' },
      { path: '/triage', label: 'Triage', icon: 'triage' },
    ],
  },
  {
    label: 'Data',
    items: [
      { path: '/offers', label: 'Offers', icon: 'offers' },
      { path: '/clusters', label: 'Clusters', icon: 'clusters' },
      { path: '/graph', label: 'Proximity graph', icon: 'graph' },
    ],
  },
]

export function AdminShell() {
  const { isAuthenticated, logout } = useAdminAuth()

  if (!isAuthenticated) return <AuthGate />

  return (
    <div className={styles.shell}>
      <Sidebar onLogout={logout} />
      <MobileHeader onLogout={logout} />
      <main className={styles.main}>
        <Switch>
          <Route path="/sources" component={SourceBreakdown} />
          <Route path="/clusters/:id" component={Clusters} />
          <Route path="/clusters" component={Clusters} />
          <Route path="/graph" component={ClusterGraph} />
          <Route path="/offers" component={Offers} />
          <Route path="/runs" component={ScrapeRuns} />
          <Route path="/triage" component={Disagreements} />
          <Route path="/" component={Overview} />
        </Switch>
      </main>
    </div>
  )
}

function Sidebar({ onLogout }) {
  const [location] = useLocation()

  return (
    <aside className={styles.sidebar} aria-label="Admin navigation">
      <div className={styles.sidebarHeader}>
        <span className={styles.sidebarTitle}>Bio Admin</span>
      </div>

      <nav className={styles.sidebarNav}>
        {NAV_GROUPS.map(group => (
          <div key={group.label} className={styles.navGroup}>
            <span className={styles.navGroupLabel}>{group.label}</span>
            {group.items.map(item => {
              const isActive = item.exact
                ? location === item.path
                : location.startsWith(item.path)

              return (
                <Link
                  key={item.path}
                  href={item.path}
                  className={`${styles.navItem} ${isActive ? styles.navItemActive : ''}`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <AdminIcon name={item.icon} size={16} />
                  {item.label}
                </Link>
              )
            })}
          </div>
        ))}
      </nav>

      <div className={styles.sidebarFooter}>
        <a
          href="/"
          className={styles.footerLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          <AdminIcon name="external" size={14} />
          View site
        </a>
        <button
          className={styles.footerLink}
          onClick={onLogout}
          type="button"
        >
          <AdminIcon name="logout" size={14} />
          Log out
        </button>
      </div>
    </aside>
  )
}

function MobileHeader({ onLogout }) {
  const [location] = useLocation()

  const allItems = NAV_GROUPS.flatMap(g => g.items)

  return (
    <div className={styles.mobileHeader}>
      <div className={styles.mobileTopRow}>
        <span className={styles.mobileTitle}>Bio Admin</span>
        <button
          className={styles.mobileLogout}
          onClick={onLogout}
          type="button"
          aria-label="Log out"
        >
          <AdminIcon name="logout" size={16} />
        </button>
      </div>
      <nav className={styles.mobileNav} aria-label="Admin navigation">
        {allItems.map(item => {
          const isActive = item.exact
            ? location === item.path
            : location.startsWith(item.path)

          return (
            <Link
              key={item.path}
              href={item.path}
              className={`${styles.mobileNavItem} ${isActive ? styles.mobileNavItemActive : ''}`}
              aria-current={isActive ? 'page' : undefined}
            >
              {item.label}
            </Link>
          )
        })}
      </nav>
    </div>
  )
}

function AuthGate() {
  const { setSecret } = useAdminAuth()
  const [input, setInput] = useState('')
  const [error, setError] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    if (!input.trim()) return

    try {
      const res = await fetch('/api/v1/admin', {
        headers: { 'X-Scrape-Secret': input.trim() },
      })

      if (res.ok) {
        setSecret(input.trim())
      } else {
        setError(true)
      }
    } catch {
      setError(true)
    }
  }

  return (
    <div className={styles.authGate}>
      <form className={styles.authForm} onSubmit={handleSubmit}>
        <div className={styles.authMark}>
          <AdminIcon name="shield" size={32} />
        </div>
        <h2 className={styles.authTitle}>Bio Admin</h2>
        <p className={styles.authSubtitle}>Enter the scrape secret to continue</p>
        <label className={styles.authLabel}>
          <span className={styles.authLabelText}>Secret</span>
          <input
            className={styles.authInput}
            type="password"
            value={input}
            onChange={e => { setInput(e.target.value); setError(false) }}
            autoFocus
          />
        </label>
        {error && (
          <p className={styles.authError} role="alert">
            Invalid secret — check the value and try again
          </p>
        )}
        <AdminButton variant="primary" type="submit" className={styles.authSubmit}>
          Unlock
        </AdminButton>
      </form>
    </div>
  )
}
